/**
 * Ritual Toggle Component
 * Allows user to optionally enable the Pre-Performance Ritual before their session.
 * Appears in the Performance Mode flow after focus area selection.
 */

import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Zap, ChevronRight } from "lucide-react";
import { containerVariants, itemVariants, getModeStyles } from "./constants";

const RitualToggle = ({ 
  sport,
  sportLabel,
  ritualEnabled,
  setRitualEnabled,
  onBack,
  onContinue,
  loading = false
}) => {
  const modeStyles = getModeStyles("performance");

  return (
    <motion.div
      key="ritual-toggle"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0 }}
      className="h-full flex flex-col p-6"
    >
      <button onClick={onBack} className="text-white/60 hover:text-white mb-6">
        <ArrowLeft size={24} />
      </button>

      <motion.div variants={itemVariants} className="mb-2">
        <span className="text-orange-400/80 text-sm uppercase tracking-wider">
          {sportLabel || "Performance Mode"}
        </span>
      </motion.div>
      
      <motion.h2 
        variants={itemVariants}
        className="font-heading text-2xl text-white mb-2"
      >
        Prime Your Mind First?
      </motion.h2>
      <motion.p 
        variants={itemVariants}
        className="text-white/50 text-sm mb-8"
      >
        A 2-minute mental preparation ritual to activate your peak state
      </motion.p>

      <motion.div variants={itemVariants} className="space-y-3 mb-8">
        {/* Yes - With Ritual */}
        <button
          onClick={() => setRitualEnabled(true)}
          className={`w-full p-5 rounded-2xl text-left transition-all ${
            ritualEnabled
              ? "bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/40"
              : "bg-white/5 border border-transparent hover:bg-white/10"
          }`}
          data-testid="ritual-option-yes"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              ritualEnabled ? "bg-orange-500/30" : "bg-white/10"
            }`}>
              <Sparkles size={22} className={ritualEnabled ? "text-orange-300" : "text-white/50"} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className={`font-medium ${ritualEnabled ? "text-white" : "text-white/70"}`}>
                  Yes, Start with Ritual
                </h3>
                {ritualEnabled && (
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-white/40 text-sm mt-1">
                2-minute mental preparation with breathing, visualization & anchoring
              </p>
              
              {/* Ritual benefits */}
              {ritualEnabled && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-[10px] px-2 py-1 bg-white/10 rounded-full text-white/60">
                    Breathing Reset
                  </span>
                  <span className="text-[10px] px-2 py-1 bg-white/10 rounded-full text-white/60">
                    Visualization
                  </span>
                  <span className="text-[10px] px-2 py-1 bg-white/10 rounded-full text-white/60">
                    Focus Anchor
                  </span>
                </div>
              )}
            </div>
          </div>
        </button>

        {/* No - Skip Ritual */}
        <button
          onClick={() => setRitualEnabled(false)}
          className={`w-full p-5 rounded-2xl text-left transition-all ${
            !ritualEnabled
              ? "bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/40"
              : "bg-white/5 border border-transparent hover:bg-white/10"
          }`}
          data-testid="ritual-option-no"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              !ritualEnabled ? "bg-orange-500/30" : "bg-white/10"
            }`}>
              <Zap size={22} className={!ritualEnabled ? "text-orange-300" : "text-white/50"} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className={`font-medium ${!ritualEnabled ? "text-white" : "text-white/70"}`}>
                  No, Dive Right In
                </h3>
                {!ritualEnabled && (
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-white/40 text-sm mt-1">
                Jump straight into your affirmations and music
              </p>
            </div>
          </div>
        </button>
      </motion.div>

      {/* Info box about rituals */}
      {ritualEnabled && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-4 rounded-xl bg-white/5 border border-white/10"
        >
          <p className="text-white/60 text-xs leading-relaxed">
            <span className="text-orange-400 font-medium">Why rituals work:</span>{" "}
            Elite athletes use pre-performance routines to prime their nervous system. 
            Over time, the ritual becomes a psychological trigger that instantly activates 
            your peak performance state.
          </p>
        </motion.div>
      )}

      {/* Continue Button */}
      <motion.button
        variants={itemVariants}
        onClick={onContinue}
        disabled={loading}
        className="w-full py-4 rounded-full font-medium text-white bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="ritual-continue-btn"
      >
        {loading ? "Loading..." : (
          <>
            Continue
            <ChevronRight size={18} />
          </>
        )}
      </motion.button>
    </motion.div>
  );
};

export default RitualToggle;
